/**
 * Client-side contact normalization + SHA-256 hashing.
 * All hashing happens in the browser. The server only ever sees hex digests.
 */

/**
 * Best-effort E.164 phone normalization.
 * - Strips spaces, dashes, parens, dots.
 * - If number starts with `+`, trust it.
 * - If starts with `00`, replace with `+`.
 * - Otherwise, prefix with the user's `countryCallingCode` (digits, e.g. "1", "44").
 * Returns null when input cannot be coerced into a plausible E.164 number.
 */
export function normalizePhone(
  raw: string,
  countryCallingCode?: string | null,
): string | null {
  if (!raw) return null;
  let s = raw.replace(/[\s\-().]/g, "");
  if (!s) return null;

  if (s.startsWith("00")) s = "+" + s.slice(2);

  if (!s.startsWith("+")) {
    const cc = (countryCallingCode || "").replace(/\D/g, "");
    if (!cc) return null; // can't normalize without a country code
    // Drop a leading 0 (common trunk prefix outside the US) before prefixing.
    if (s.startsWith("0")) s = s.slice(1);
    s = `+${cc}${s.replace(/\D/g, "")}`;
  } else {
    s = "+" + s.slice(1).replace(/\D/g, "");
  }

  // Plausibility: 8–15 digits after the +
  const digits = s.slice(1);
  if (digits.length < 8 || digits.length > 15) return null;
  return s;
}

export function normalizeEmail(raw: string): string | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return null;
  return s;
}

/** SHA-256 hex digest of a UTF-8 string. */
export async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function hashMany(values: string[]): Promise<string[]> {
  return Promise.all(values.map(sha256Hex));
}

/** Normalized contact shape used after reading from Despia's contacts bridge. */
export interface NativeContact {
  name?: string;
  phones?: string[];
  emails?: string[];
}

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  return typeof value === "string" ? [value] : [];
};

/**
 * Despia's documented `readcontacts://` response is an object keyed by contact
 * name with phone arrays as values. Older/internal builds may return an array of
 * `{ name, phones, emails }`. Accept both so app-store builds don't fall back to
 * "not available" just because the native shape differs.
 */
export function normalizeNativeContacts(payload: unknown): NativeContact[] {
  if (!payload) return [];

  if (typeof payload === "string") {
    try {
      return normalizeNativeContacts(JSON.parse(payload));
    } catch {
      return [];
    }
  }

  if (Array.isArray(payload)) {
    return payload
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const record = item as Record<string, unknown>;
        const contact: NativeContact = {
          name: typeof record.name === "string" ? record.name : undefined,
          phones: toStringArray(record.phones ?? record.phoneNumbers ?? record.numbers ?? record.phone),
          emails: toStringArray(record.emails ?? record.emailAddresses ?? record.email),
        };
        return contact.phones?.length || contact.emails?.length ? contact : null;
      })
      .filter((contact): contact is NativeContact => contact !== null);
  }

  if (typeof payload === "object") {
    return Object.entries(payload as Record<string, unknown>)
      .map(([name, value]) => {
        if (Array.isArray(value) || typeof value === "string") {
          return { name, phones: toStringArray(value), emails: [] };
        }
        if (value && typeof value === "object") {
          const record = value as Record<string, unknown>;
          const contact: NativeContact = {
            name,
            phones: toStringArray(record.phones ?? record.phoneNumbers ?? record.numbers ?? record.phone),
            emails: toStringArray(record.emails ?? record.emailAddresses ?? record.email),
          };
          return contact.phones?.length || contact.emails?.length ? contact : null;
        }
        return null;
      })
      .filter((contact): contact is NativeContact => contact !== null);
  }

  return [];
}

/**
 * Normalize + de-dupe a list of native contacts into two arrays of E.164 phones
 * and lowercased emails ready to be hashed.
 */
export function flattenContacts(
  contacts: NativeContact[],
  countryCallingCode?: string | null,
): { phones: string[]; emails: string[] } {
  const phones = new Set<string>();
  const emails = new Set<string>();
  for (const c of contacts) {
    for (const p of c.phones ?? []) {
      const n = normalizePhone(p, countryCallingCode);
      if (n) phones.add(n);
    }
    for (const e of c.emails ?? []) {
      const n = normalizeEmail(e);
      if (n) emails.add(n);
    }
  }
  return { phones: Array.from(phones), emails: Array.from(emails) };
}
